import { useState, useRef, useEffect } from "react";
import { SuperAdminLayout } from "@/components/admin/SuperAdminLayout";
import { Card, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { useAuth } from "@/hooks/useAuth";
import { 
  type Message,
  callGraphRAG,
  processGraphRAGResponse,
  createWelcomeMessage,
  createErrorMessage
} from "@/lib/askAiUtils";
import { ChatMessage } from "@/components/admin/ChatMessage";
import { ChatLoadingIndicator } from "@/components/admin/ChatLoadingIndicator";
import { Send, RefreshCw, Sparkles } from "lucide-react";

export default function AskAI() {
  const { currentUser } = useAuth();
  const [message, setMessage] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [conversation, setConversation] = useState<Message[]>([createWelcomeMessage()]);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const chatContainerRef = useRef<HTMLDivElement>(null);

  const handleSendMessage = async (messageText?: string) => {
    const textToSend = messageText || message;
    if (!textToSend.trim() || !currentUser) return;

    setIsLoading(true);
    
    // Add user message
    const userMessage: Message = {
      id: `user-${Date.now()}`,
      type: 'user',
      content: textToSend,
      timestamp: new Date().toISOString()
    };

    setConversation(prev => [...prev, userMessage]);
    setMessage("");

    try {
      // Call GraphRAG and process response
      const responseData = await callGraphRAG(textToSend, 15);
      const aiResponse = await processGraphRAGResponse(responseData, textToSend);
      
      setConversation(prev => [...prev, aiResponse]);
    } catch (error) {
      const errorResponse = createErrorMessage(error);
      setConversation(prev => [...prev, errorResponse]);
    } finally {
      setIsLoading(false);
    }
  };

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  const clearConversation = () => {
    setConversation([createWelcomeMessage()]);
  };

  // Auto-scroll when new messages are added
  useEffect(() => {
    scrollToBottom();
  }, [conversation, isLoading]);

  return (
    <SuperAdminLayout 
      title="Aria - AI Assistant" 
      subtitle="Got GraphRAG? Aria does!"
      maxWidth="full"
      contentClassName="p-4 lg:p-6 pt-16 lg:pt-6"
      headerClassName="mb-4 lg:mb-6"
    >
      {/* Header Actions */}
      <div className="flex justify-end mb-4 lg:mb-6">
        <Button 
          onClick={clearConversation}
          variant="secondary"
          size="sm"
          className="font-medium"
          data-testid="button-clear-chat"
        >
          <RefreshCw className="h-3 w-3 mr-2" />
          Clear
        </Button>
      </div>

      {/* Chat Interface - Full Screen */}
      <div className="h-[calc(100vh-200px)]">
        <Card className="border-0 shadow-none h-full flex flex-col">
          <CardHeader className="border-b py-3 px-4 flex-shrink-0">
            <div className="flex items-center justify-between">
              <CardTitle className="flex items-center gap-2 text-lg">
                <Sparkles className="h-4 w-4 text-purple-600" />
                Chat with Aria
              </CardTitle>
            </div>
          </CardHeader>

          {/* Messages */}
          <div 
            ref={chatContainerRef}
            className="flex-1 overflow-y-auto p-6 space-y-4 min-h-0"
            style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}
          >
            {conversation.map((msg) => (
              <ChatMessage 
                key={msg.id} 
                message={msg} 
                onSendMessage={handleSendMessage} 
              />
            ))}
            
            {isLoading && <ChatLoadingIndicator />}
            <div ref={messagesEndRef} />
          </div>

          {/* Input */}
          <div className="border-t p-4 flex-shrink-0">
            <div className="flex gap-3">
              <div className="flex-1">
                <Input
                  value={message}
                  onChange={(e) => setMessage(e.target.value)}
                  placeholder="Ask Aria about candidates in our talent pool..."
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' && !e.shiftKey) {
                      e.preventDefault();
                      handleSendMessage();
                    }
                  }}
                  disabled={isLoading}
                      className="resize-none"
                  data-testid="ai-message-input"
                />
              </div>
              <Button
                onClick={() => handleSendMessage()}
                disabled={isLoading || !message.trim()}
                className="bg-orange-500 hover:bg-orange-600 text-white px-4"
                data-testid="send-message"
              >
                <Send className="h-4 w-4" />
              </Button>
            </div>
          </div>
        </Card>
      </div>
    </SuperAdminLayout>
  );
}